import { useState, useEffect, useMemo, useCallback } from 'react';
import { ScanLine, Search } from 'lucide-react';
import { rpc } from '@/api/client';
import { useI18n } from '@/context/I18nContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { BarcodeScanner, isCameraAvailable } from './BarcodeScanner';
import type { TyreSetInfo, ListTyreSetsResponse, LookupTyreByBarcodeResponse } from '@/types/telemetry';

interface TyreSelectorProps {
  onSelect: (tyreId: string) => void;
}

export function TyreSelector({ onSelect }: TyreSelectorProps) {
  const { t } = useI18n();
  const [tyres, setTyres] = useState<TyreSetInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const cameraAvailable = isCameraAvailable();

  useEffect(() => {
    let cancelled = false;
    rpc<ListTyreSetsResponse>('ListTyreSets', { recentlyUsedLimit: 3 }).then(resp => {
      if (!cancelled) {
        setTyres(resp.tyres || []);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const handleScanSuccess = useCallback(async (barcode: string) => {
    setShowScanner(false);
    try {
      const resp = await rpc<LookupTyreByBarcodeResponse>('LookupTyreByBarcode', { barcode });
      const results = resp.tyres || [];
      if (results.length === 1) {
        onSelect(results[0].id);
      } else if (results.length > 1) {
        // Filter the list to matching tyres
        setSearchQuery(barcode);
      } else {
        setSearchQuery(barcode);
      }
    } catch {
      setSearchQuery(barcode);
    }
  }, [onSelect]);

  const filteredTyres = useMemo(() => {
    if (!searchQuery.trim()) return tyres;
    const q = searchQuery.toLowerCase().trim();
    return tyres.filter(t =>
      t.brand.toLowerCase().includes(q) ||
      t.type.toLowerCase().includes(q) ||
      t.nr.toLowerCase().includes(q) ||
      t.compound.toLowerCase().includes(q) ||
      t.tyreIdFl.toLowerCase().includes(q) ||
      t.tyreIdFr.toLowerCase().includes(q) ||
      t.tyreIdRl.toLowerCase().includes(q) ||
      t.tyreIdRr.toLowerCase().includes(q)
    );
  }, [tyres, searchQuery]);

  // Only show active (non-discarded) tyres
  const activeTyres = filteredTyres.filter(t => !t.discarded);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="flex flex-col gap-4">
      {/* Search input */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={t('tyre.barcodeSearch')}
            className="input-field w-full pl-9 pr-3 py-2 text-sm"
          />
        </div>
        {cameraAvailable && (
          <button
            onClick={() => setShowScanner(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium bg-accent text-white hover:bg-accent-hover transition-colors cursor-pointer shrink-0"
          >
            <ScanLine size={14} />
            {t('tyre.scanBarcode')}
          </button>
        )}
      </div>

      {/* Tyre list */}
      {activeTyres.length === 0 ? (
        <div className="card flex flex-col items-center justify-center py-12 gap-2 text-center">
          <span className="text-2xl opacity-60">🔍</span>
          <p className="text-sm text-text-secondary">{t('tyre.noTyresFound')}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {activeTyres.map(tyre => (
            <TyreCard key={tyre.id} tyre={tyre} onSelect={onSelect} t={t} dimmed={false} />
          ))}
        </div>
      )}

      {/* Scanner modal */}
      {showScanner && (
        <BarcodeScanner
          onScanSuccess={handleScanSuccess}
          onClose={() => setShowScanner(false)}
        />
      )}
    </div>
  );
}

function TyreCard({ tyre, onSelect, t, dimmed }: {
  tyre: TyreSetInfo;
  onSelect: (id: string) => void;
  t: (key: any) => string;
  dimmed: boolean;
}) {
  const isDry = tyre.compound.toLowerCase() === 'dry';
  const compoundLabel = isDry ? t('tyre.dry') : t('tyre.wet');

  return (
    <button
      onClick={() => onSelect(tyre.id)}
      className={`card-interactive p-4 text-left w-full hover:bg-bg-hover hover:border-accent/50 ${dimmed ? 'opacity-40' : ''}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-text-primary">{tyre.brand} {tyre.type}</span>
          <span
            className={`inline-block px-2 py-0.5 rounded-full text-[0.68rem] font-medium border ${
              isDry
                ? 'bg-green/10 text-green border-green/20'
                : 'bg-blue-400/10 text-blue-400 border-blue-400/20'
            }`}
          >
            {compoundLabel}
          </span>
        </div>
        <span className="text-xs text-text-secondary">#{tyre.nr}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-0.5">
        <span className="text-[0.68rem] text-text-muted">FL: <span className="mono text-text-secondary">{tyre.tyreIdFl || '—'}</span></span>
        <span className="text-[0.68rem] text-text-muted">FR: <span className="mono text-text-secondary">{tyre.tyreIdFr || '—'}</span></span>
        <span className="text-[0.68rem] text-text-muted">RL: <span className="mono text-text-secondary">{tyre.tyreIdRl || '—'}</span></span>
        <span className="text-[0.68rem] text-text-muted">RR: <span className="mono text-text-secondary">{tyre.tyreIdRr || '—'}</span></span>
      </div>
    </button>
  );
}
